import { type TeamAnalysisHomeAway, type StandingTeam } from "@/api/tournamentAnalysis";
import { TournamentStandingsTable } from "./TournamentStandingsTable";
import { Loader } from "../common/loader";

export type StandingsSubTab = "overall" | "home" | "away" | "playoffs";

interface TournamentAnalysisHomeAwayPerformanceProps {
    activeTab: "standings" | "home-away";
    onActiveTabChange: (tab: "standings" | "home-away") => void;
    standingsSubTab: StandingsSubTab;
    onStandingsSubTabChange: (tab: StandingsSubTab) => void;
    homeAwayData?: TeamAnalysisHomeAway;
    homeAwayLoading?: boolean;
    overallStandings?: StandingTeam[];
    homeStandings?: StandingTeam[];
    awayStandings?: StandingTeam[];
    standingsLoading?: boolean;
    isPlayoffTournament?: boolean;
}

const STAT_ROWS = [
    { label: "Win Rate", shortLabel: "WR", key: "winRate", format: (v: number) => `${v.toFixed(1)}%` },
    { label: "Goals / Game", shortLabel: "G per G", key: "goalsPerGame", format: (v: number) => v.toFixed(1) },
    {
        label: "Goals Against / Game",
        shortLabel: "GA per G",
        key: "goalsAgainstPerGame",
        format: (v: number) => v.toFixed(1),
    },
    { label: "Games Played", shortLabel: "GP", key: "gamesPlayed", format: (v: number) => v.toString() },
    { label: "Record", shortLabel: "Record", key: "recordWin", format: (v: number | string) => String(v) },
] as const;

export function TournamentAnalysisHomeAwayPerformance({
    activeTab,
    onActiveTabChange,
    standingsSubTab,
    onStandingsSubTabChange,
    homeAwayData,
    homeAwayLoading,
    overallStandings,
    homeStandings,
    awayStandings,
    standingsLoading,
    isPlayoffTournament,
}: TournamentAnalysisHomeAwayPerformanceProps) {
    const standingsSubTabs = isPlayoffTournament
        ? [{ id: "playoffs" as StandingsSubTab, label: "Playoffs" }]
        : [
              { id: "overall" as StandingsSubTab, label: "Overall" },
              { id: "home" as StandingsSubTab, label: "Home" },
              { id: "away" as StandingsSubTab, label: "Away" },
          ];

    const home = homeAwayData?.homeAwayComparison?.home;
    const away = homeAwayData?.homeAwayComparison?.away;
    const hasHomeAwayData = !!homeAwayData?.homeAwayComparison;

    return (
        <div id="onboarding-tournament-home-away" className="flex flex-col flex-1 gap-3">
            <div className="flex flex-col gap-2 shrink-0 md:hidden">
                <nav className="rounded-md p-1 bg-surface-container-low md:hidden">
                    <div className="grid grid-cols-2 gap-1">
                        {(
                            [
                                { id: "standings" as const, label: "Standings" },
                                { id: "home-away" as const, label: "Performance" },
                            ] as const
                        ).map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                className={`nav-tab ${activeTab === tab.id ? "nav-tab-active" : "nav-tab-inactive"}`}
                                onClick={() => onActiveTabChange(tab.id)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </nav>
                {activeTab === "standings" && standingsSubTabs.length > 1 && (
                    <nav className="rounded-md p-1 bg-surface-container-low md:hidden">
                        <div className="grid grid-cols-3 gap-1">
                            {standingsSubTabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    type="button"
                                    className={`nav-tab ${standingsSubTab === tab.id ? "nav-tab-active" : "nav-tab-inactive"}`}
                                    onClick={() => onStandingsSubTabChange(tab.id)}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                    </nav>
                )}
            </div>
            <div className="bg-surface-container-lowest rounded-xl overflow-hidden shadow-sm flex flex-col flex-1">
                {activeTab === "home-away" ? (
                    <>
                        {homeAwayLoading && !homeAwayData ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader message="Loading performance data..." />
                            </div>
                        ) : hasHomeAwayData ? (
                            <table className="w-full border-collapse">
                                <thead>
                                    <tr className="border-0">
                                        <th
                                            className="data-table-header px-5 py-3.5 font-headline font-black text-[10px] uppercase tracking-widest text-left whitespace-nowrap sticky top-0 z-10 w-[40%]"
                                            style={{
                                                color: "var(--color-primary-token)",
                                                borderBottom: "2px solid var(--color-primary-token)",
                                            }}
                                        >
                                            Home
                                        </th>
                                        <th className="data-table-header px-4 py-3.5 font-headline font-black text-[10px] uppercase tracking-widest text-center whitespace-nowrap sticky top-0 z-10">
                                            Stat
                                        </th>
                                        <th
                                            className="data-table-header px-5 py-3.5 font-headline font-black text-[10px] uppercase tracking-widest text-right whitespace-nowrap sticky top-0 z-10 w-[40%]"
                                            style={{
                                                color: "var(--color-secondary-token)",
                                                borderBottom: "2px solid var(--color-secondary-token)",
                                            }}
                                        >
                                            Away
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {STAT_ROWS.map(({ label, shortLabel, key, format }, idx) => {
                                        const homeVal = home ? format(home[key] as never) : "—";
                                        const awayVal = away ? format(away[key] as never) : "—";
                                        const rowBg =
                                            idx % 2 === 0 ? "bg-surface-container-lowest" : "bg-surface-container-low";
                                        return (
                                            <tr
                                                key={label}
                                                className={`data-table-row transition-colors border-0 ${rowBg}`}
                                            >
                                                <td
                                                    className="px-5 py-4 text-xl font-headline font-black text-left whitespace-nowrap"
                                                    style={{ color: "var(--color-primary-token)" }}
                                                >
                                                    {homeVal}
                                                </td>
                                                <td className="px-4 py-4 text-[10px] font-headline font-bold uppercase tracking-wider text-on-surface-variant text-center whitespace-nowrap">
                                                    <span className="md:hidden">{shortLabel || label}</span>
                                                    <span className="hidden md:inline">{label}</span>
                                                </td>
                                                <td
                                                    className="px-5 py-4 text-xl font-headline font-black text-right whitespace-nowrap"
                                                    style={{ color: "var(--color-secondary-token)" }}
                                                >
                                                    {awayVal}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        ) : (
                            <div className="text-center text-on-surface-variant py-8 text-sm">
                                No home/away data available
                            </div>
                        )}
                    </>
                ) : (
                    <TournamentStandingsTable
                        activeTab={standingsSubTab}
                        overallStandings={overallStandings}
                        homeStandings={homeStandings}
                        awayStandings={awayStandings}
                        isLoading={standingsLoading}
                    />
                )}
            </div>
        </div>
    );
}
